import { ref, nextTick, computed, getCurrentInstance } from '@vue/composition-api'
import axios from '@/libs/axios';
import store from "@/store";
import { useToast } from "vue-toastification/composition";
import { useUtils as useI18nUtils } from "@core/libs/i18n";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export function useSignificantEvents() {
  const toast = useToast();
  const { t } = useI18nUtils();
  const vm = getCurrentInstance().proxy;
  const significantEvents = ref([])
  const significantEvent = ref(null)
  const overflowingCells = ref({});
  const perPage = ref(10)
  const totalList = ref(0)
  const currentPage = ref(1)
  const perPageOptions = [10, 25, 50, 100]
  const dateWillcardsOptions = ['last24h', 'last48h', 'lastWeek', 'lastMonth']
  const showFilters = ref(false);
  const currentDateWillcard = ref(null);
  const fromDate = ref('');
  const toDate = ref('');
  const isDataLoading = ref(true);
  const isShowDetails = ref(false);
  const canStoreSignificantEvent = ref(false);
  const isBeingSavedSignificantEvent = ref(false);
  const translations = {
    "operational_security.significant_event_save_success": t(
      "operational_security.significant_event_save_success"
    ),
  };
  const supervisorProfiles = [
    "SuperAdmin",
    "supervisor_monitoreo"
  ];
  const datePickerConfig = {
    dateFormat: "Y-m-d H:i:S",
    enableTime: true,
    time_24hr: true,
    allowInput: true,
    locale: {
      firstDayOfWeek: 1,
    },
  };
  const user = store.getters["user/getUser"];
  const isUserSupervisor = isSupervisor(user.profile.type);
  const isSuperAdmin = computed(() => user.profile.type === "SuperAdmin");
  const isRegionalDirector = computed(() => user.profile.type === "director_regional");
  const isDark = computed(() => store.state.appConfig.layout.skin === "dark");

  function isSupervisor(profile) {
    return supervisorProfiles.includes(profile);
  }

  const getsignificantEvents = () => {
    isDataLoading.value = true
    const payload = {
      // q: searchQuery.value,
      max: perPage.value,
      'page[n]': currentPage.value,
    }
    if(fromDate.value) {
      payload['from'] = fromDate.value;
    }
    if(toDate.value) {
      payload['to'] = toDate.value;
    }
    if(currentDateWillcard.value) {
      payload[currentDateWillcard.value] = true;
    }
    axios
    .get(`/v1/operational-security-forms/significant-events`, { params: payload })
    .then((response) => {
      significantEvents.value = response?.data?.data
      totalList.value = response?.meta?.total
      isDataLoading.value = false
    })
  };

  const getsignificantEvent = (id) => {
    axios
    .get(`/v1/operational-security-forms/significant-events/${id}`)
    .then((response) => {
      significantEvent.value = response?.data?.data
    })
  };

  const checkOverflow = () => {
    nextTick(() => {
      overflowingCells.value = {};

      significantEvents.value.forEach((event, index) => {
        [
          `description-${event.id}-${index}`,
          `director-comments-${event.id}-${index}`,
        ].forEach(id => {
          const el = document.getElementById(id);

          if (el) {
            overflowingCells.value[id] =
              el.scrollWidth > el.clientWidth ||
              el.scrollHeight > el.clientHeight;
          }
        });
      });
    });
  };

  const update = (data) => {
    currentPage.value = data;
  };

  const showFiltersToggle = (data) => {
    showFilters.value = !showFilters.value;
  };

  const clearDatesRanges = () => {
    fromDate.value = '';
    toDate.value = '';
    currentDateWillcard.value = null;
  };

  const handleDateWillcardChange = (dateType, checked) => {
    currentDateWillcard.value = checked ? dateType : null;
  };

  const exportSignificantEvents = type => {
    const payload = {
      type: type,
    };

    axios
      .get('/v1/operational-security-forms/significant-events/export', {
        params: payload,
        responseType: 'arraybuffer',
        headers: {
          Accept: type === 'pdf'
            ? 'application/pdf'
            : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      })
      .then(response => {
        const contentType = response.headers['content-type'];

        const blob = new Blob([response.data], {
          type: contentType || (
            type === 'pdf'
              ? 'application/pdf'
              : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          ),
        });

        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        const now = new Date();
        const pad = n => String(n).padStart(2, '0');

        const fileName = `evento-relevante-${
          now.getFullYear()
        }-${
          pad(now.getMonth() + 1)
        }-${
          pad(now.getDate())
        }_${
          pad(now.getHours())
        }-${
          pad(now.getMinutes())
        }-${
          pad(now.getSeconds())
        }`;

        link.download =
          type === 'pdf'
            ? `${fileName}.pdf`
            : `${fileName}.xlsx`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        window.URL.revokeObjectURL(url);
      });
  };

  const goBack = () => {
    isShowDetails.value = false;
    significantEvent.value = null;
    significantEvents.value = [];
    getsignificantEvents()
  };

  const showDetails = (significantEvt) => {
    significantEvent.value = significantEvt;
    isShowDetails.value = true;

    canStoreSignificantEvent.value =
      significantEvent.value?.form_answer?.id_user_validator === user.id ||
      significantEvent.value?.id_security_director === user.id;
  };

  const goToLinkedForm = () => {
    const route = vm.$router.resolve({
      name: 'OperationalSecurityFormAnswers',
      query: { id: significantEvent.value.form_answer.id },
    })
    window.open(route.href, '_blank');
  }

  const updateSignificantEvent = () => {
    isBeingSavedSignificantEvent.value = true
    let payload = {}

    if(isUserSupervisor) {
      payload.significantEventTitle = significantEvent.value.subject
      payload.significantEventDescription = significantEvent.value.description
    } else {
      payload.significantSecurityManagerComments = significantEvent.value.director_comments
    }

    axios.put(`/v1/operational-security-forms/significant-events/${significantEvent.value.id}`, payload)
    .then((response) => {
      contentToast(translations["operational_security.significant_event_save_success"], "success");
    })
    .catch((e) => {
      contentToast(e.response?.data?.message ?? 'Error on save Significant Event', "danger", "AlertTriangleIcon");
    })
    .finally(()=> {
      isBeingSavedSignificantEvent.value = false
    })
  };

  function contentToast(message, type, icon = "CheckIcon") {
    toast({
      component: ToastificationContent,
      props: {
        title: message,
        icon,
        variant: type,
      },
    });
  }

  return {
    significantEvents,
    significantEvent,
    overflowingCells,
    perPage,
    perPageOptions,
    totalList,
    currentPage,
    showFilters,
    dateWillcardsOptions,
    currentDateWillcard,
    toDate,
    fromDate,
    datePickerConfig,
    isDataLoading,
    isShowDetails,
    isDark,
    canStoreSignificantEvent,
    isUserSupervisor,
    isSuperAdmin,
    isRegionalDirector,
    isBeingSavedSignificantEvent,
    getsignificantEvents,
    checkOverflow,
    update,
    showFiltersToggle,
    handleDateWillcardChange,
    clearDatesRanges,
    exportSignificantEvents,
    showDetails,
    goBack,
    goToLinkedForm,
    updateSignificantEvent
  }
}
